import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";
import termsHero from "@/assets/terms-hero.jpg";

const Terms = () => {
  return (
    <Layout>
      <SEO 
        title="Terms of Service - Random Number Generator"
        description="Read our terms of service for using our free random number generator and related tools."
        canonical="https://numbers-generator.com/terms"
      />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            RandomGen Numbers Generator Terms of Service
          </h1>
          <p className="text-sm text-muted-foreground">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </header>

        <div className="space-y-6">
          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Agreement to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing and using RandomGen, you accept and agree to be bound by the terms and provisions of this 
              agreement. If you do not agree to these terms, please do not use our services.
            </p>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Use of Services</h2>
            <div className="space-y-4 text-muted-foreground">
              <p className="leading-relaxed">
                RandomGen provides random number generation tools and related services. You may use these services for:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Personal use, including games and entertainment</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Professional applications such as research and data analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Educational purposes and demonstrations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Fair contests, lotteries, and prize drawings</span>
                </li>
              </ul>
            </div>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Prohibited Activities</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Use our services for any illegal or unauthorized purpose</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Attempt to reverse engineer or compromise our systems</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Use automated tools to abuse or overload our services</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Impersonate others or provide false information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Interfere with other users' access to our services</span>
              </li>
            </ul>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Randomness and Fair Use</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              While our random number generator uses cryptographically secure methods, we provide these services 
              "as is" for general use. For critical applications:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Verify results meet your specific requirements</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Use appropriate validation and testing procedures</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Implement additional safeguards as needed for your use case</span>
              </li>
            </ul>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Account Registration</h2>
            <p className="text-muted-foreground leading-relaxed">
              Creating an account is optional. If you choose to register, you are responsible for maintaining the 
              security of your account credentials. You agree to provide accurate information and update it as needed.
            </p>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              All content, features, and functionality on RandomGen are owned by us and protected by international 
              copyright, trademark, and other intellectual property laws. The random numbers you generate are yours 
              to use freely.
            </p>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Disclaimer of Warranties</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              RandomGen is provided "as is" without warranties of any kind, either express or implied. We do not 
              guarantee that:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>The service will be uninterrupted or error-free</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Results will meet your specific needs</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>All errors will be corrected</span>
              </li>
            </ul>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              To the fullest extent permitted by law, RandomGen shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages resulting from your use or inability to use our services.
            </p>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Service Modifications</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify, suspend, or discontinue any part of our services at any time without 
              notice. We may also update these terms periodically, and continued use constitutes acceptance of changes.
            </p>
          </Card>

          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These terms are governed by applicable laws. Any disputes arising from these terms or use of our 
              services shall be resolved through appropriate legal channels.
            </p>
          </Card>

          <Card className="p-8 bg-accent/50">
            <h2 className="text-2xl font-semibold mb-4">Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              Questions about these Terms of Service? Please visit our{' '}
              <a href="/contact" className="text-primary hover:underline">contact page</a>.
            </p>
          </Card>
        </div>

        {/* Hero Image at Bottom */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={termsHero} 
            alt="Legal terms of service and contract documents showing professional business policies and agreements for RandomGen random number generator service"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default Terms;
